import type { TFunction } from 'i18next'

import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import type { SidebarNavChild, SidebarNavItem } from '@/components/layout/app-shell'
import {
  SIDEBAR_CONVERSATION_LIMIT,
  conversationLabel,
} from '@/features/shared/chat/conversation/conversation-utils'
import { PinFilledIcon, PinOutlineIcon } from '@/features/shared/chat/conversation/pin-icons'
import { AppMarketIcon, CoreAgentIcon } from '@/features/user/shell/navigation-icons'
import type { BusinessNavigationLink } from '@/features/user/shell/business-navigation'
import { SIDEBAR_APP_LIST_EXPANDED_LIMIT } from '@/features/user/apps/navigation-order'
import {
  makeBusinessNavigationItems,
  makeUserInstalledAppNavItems,
  type SidebarUserApp,
} from '@/features/user/shell/navigation'
import { cn } from '@/lib/cn'

import type { RuntimeConversationDto } from '@/features/shared/chat/types'

export function buildChatPath(appId: string, conversationId?: string) {
  const query = new URLSearchParams({ appId })

  if (conversationId) {
    query.set('conversationId', conversationId)
  }

  return `/chat?${query.toString()}`
}

export function buildChatHistoryPath(appId: string) {
  const query = new URLSearchParams({ appId, view: 'history' })

  return `/chat?${query.toString()}`
}

function renderActionAssetIcon({ className, src }: { className?: string; src: string }) {
  return <img alt="" aria-hidden className={cn('size-3.5 shrink-0', className)} src={src} />
}

function renderEditIcon() {
  return renderActionAssetIcon({ src: actionEditIconUrl })
}

function renderTrashIcon() {
  return renderActionAssetIcon({ className: 'h-3.5 w-[13px]', src: actionDeleteIconUrl })
}

/**
 * 统一组装 chat 侧栏导航，避免 page 同时承担路由、应用入口和会话 action 的结构细节。
 */
export function makeConversationNavigation({
  appId,
  apps,
  businessNavigationLink = null,
  mobileApps,
  conversations,
  hubAgentName,
  loadingApps,
  loading,
  onRequestDelete,
  onRequestRename,
  onTogglePin,
  showChatRuntime,
  showHubAgent,
  t,
}: {
  appId?: string
  apps: SidebarUserApp[]
  businessNavigationLink?: BusinessNavigationLink | null
  mobileApps?: SidebarUserApp[]
  conversations: RuntimeConversationDto[]
  hubAgentName: string
  loadingApps: boolean
  loading: boolean
  onRequestDelete: (conversation: RuntimeConversationDto) => void
  onRequestRename: (conversation: RuntimeConversationDto) => void
  onTogglePin: (conversation: RuntimeConversationDto) => void
  showChatRuntime: boolean
  showHubAgent: boolean
  t: TFunction
}): SidebarNavItem[] {
  const currentAppNewChatPath = appId ? buildChatPath(appId) : '/chat'
  const currentAppHistoryPath = appId ? buildChatHistoryPath(appId) : '/chat'
  const conversationChildren: SidebarNavChild[] =
    conversations.length > 0
      ? conversations.slice(0, SIDEBAR_CONVERSATION_LIMIT).map((conversation) => ({
          key: `conversation-flyout-${conversation.id}`,
          label: conversationLabel(conversation, t('sidebar.untitled')),
          path: appId ? buildChatPath(appId, conversation.id) : '/chat',
        }))
      : [
          {
            activeBehavior: 'never' as const,
            key: 'conversation-flyout-empty',
            label: t(appId ? 'sidebar.empty' : 'sidebar.pickApp'),
            path: currentAppNewChatPath,
          },
        ]

  if (conversations.length > SIDEBAR_CONVERSATION_LIMIT) {
    conversationChildren.push({
      key: 'conversation-flyout-view-all',
      label: t('sidebar.viewAll'),
      path: currentAppHistoryPath,
      variant: 'strong',
    })
  }

  const visibleConversations = conversations.slice(0, SIDEBAR_CONVERSATION_LIMIT)

  const appNavItems: SidebarNavItem[] = [
    ...(showHubAgent
      ? [
          {
            activeMatch: 'hub-chat' as const,
            icon: <CoreAgentIcon />,
            key: 'core-agent',
            label: hubAgentName,
            path: '/chat',
            sticky: 'top' as const,
            variant: 'ghost-prominent' as const,
          },
        ]
      : []),
    {
      icon: <AppMarketIcon />,
      key: 'app-market',
      label: t('shell:userNavigation.appMarket'),
      path: '/apps',
      sticky: 'top',
      variant: 'ghost-prominent',
    },
    ...makeBusinessNavigationItems(businessNavigationLink),
    ...makeUserInstalledAppNavItems({
      apps,
      desktopLimit: showChatRuntime ? undefined : SIDEBAR_APP_LIST_EXPANDED_LIMIT,
      loading: loadingApps,
      mobileApps,
      t: (key) => t(`shell:${key}`),
    }),
  ]

  if (!showChatRuntime) {
    return appNavItems
  }

  return [
    ...appNavItems,
    {
      defaultExpanded: true,
      flyoutSize: 'wide',
      key: 'conversation-history-heading',
      children: loading
        ? [
            {
              activeBehavior: 'never' as const,
              key: 'conversation-flyout-loading',
              label: t('sidebar.loading'),
              path: currentAppNewChatPath,
            },
          ]
        : conversationChildren,
      kind: 'heading',
      label: t('shell:userNavigation.history'),
      spacingBefore: 'section',
    },
    ...(loading
      ? [
          {
            activeBehavior: 'never' as const,
            key: 'conversation-loading',
            label: t('sidebar.loading'),
            path: currentAppNewChatPath,
            section: 'conversation-history-heading',
            variant: 'subtle' as const,
          },
        ]
      : conversations.length > 0
        ? [
            ...visibleConversations.map((conversation) => {
              const label = conversationLabel(conversation, t('sidebar.untitled'))
              const pinned = Boolean(conversation.pinned)

              return {
                actions: [
                  {
                    icon: renderEditIcon(),
                    key: 'rename',
                    label: t('sidebar.renameConversation'),
                    onSelect: () => {
                      onRequestRename(conversation)
                    },
                  },
                  {
                    icon: pinned ? <PinFilledIcon /> : <PinOutlineIcon />,
                    key: pinned ? 'unpin' : 'pin',
                    label: t(pinned ? 'sidebar.unpinConversation' : 'sidebar.pinConversation'),
                    onSelect: () => {
                      onTogglePin(conversation)
                    },
                  },
                  {
                    icon: renderTrashIcon(),
                    key: 'delete',
                    label: t('sidebar.deleteConversation'),
                    onSelect: () => {
                      onRequestDelete(conversation)
                    },
                    tone: 'danger' as const,
                  },
                ],
                actionsLabel: t('sidebar.conversationActions', { title: label }),
                inlineStatus: pinned
                  ? {
                      icon: <PinFilledIcon />,
                      key: 'pinned',
                      label: t('sidebar.pinnedConversation', { title: label }),
                    }
                  : undefined,
                key: `conversation-${conversation.id}`,
                label,
                path: appId ? buildChatPath(appId, conversation.id) : '/chat',
                section: 'conversation-history-heading',
                variant: 'subtle' as const,
              }
            }),
            ...(conversations.length > SIDEBAR_CONVERSATION_LIMIT
              ? [
                  {
                    key: 'conversation-view-all',
                    label: t('sidebar.viewAll'),
                    path: currentAppHistoryPath,
                    section: 'conversation-history-heading',
                    variant: 'subtle-strong' as const,
                  },
                ]
              : []),
          ]
        : [
            {
              activeBehavior: 'never' as const,
              key: 'conversation-empty',
              label: t(appId ? 'sidebar.empty' : 'sidebar.pickApp'),
              path: currentAppNewChatPath,
              section: 'conversation-history-heading',
              variant: 'subtle' as const,
            },
          ]),
  ]
}
